import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const TRUST_ARC_SCRIPT_SELECTOR = 'script[data-trustarc-script="true"]';

const TrustArcIds = {
  'Sub-Domain': '3xsewy', // Used on SJP Partnership site.
  'Tier 0': '3xsewy',
  'Tier 1': '3xsewy',
  'Tier 2': 'oc4oru',
};

export interface TrustArcInstance {
  bn?: {
    openBanner?: () => void;
    bannerMain?: () => void;
  };
  eu?: {
    icon?: {
      initialize?: () => void;
    };
  };
}

interface TrustArcContextValue {
  truste: TrustArcInstance | null;
  isReady: boolean;
}

const TrustArcContext = createContext<TrustArcContextValue>({
  truste: null,
  isReady: false,
});

let loadedTrustArcId: string | null = null;

/**
 * Determines the matching TrustArc ID based on the partner level.
 * @param partnerLevel The partner level (Tier 0, Tier 1, Tier 2)
 * @returns The matching TrustArc ID
 */
export function getTrustArcId(partnerLevel: string) {
  return TrustArcIds[partnerLevel] || TrustArcIds['Tier 0'];
}

function createTrustArcScriptUrl(id: string): string {
  return `https://consent.trustarc.com/v2/notice/${id}`;
}

function getTrustArcInstance(): TrustArcInstance | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return (
    (
      window as Window & {
        truste?: TrustArcInstance;
      }
    ).truste ?? null
  );
}

function findTrustArcScript(scriptUrl: string): HTMLScriptElement | undefined {
  const scripts = document.querySelectorAll<HTMLScriptElement>(TRUST_ARC_SCRIPT_SELECTOR);

  return Array.from(scripts).find((script) => script.src === scriptUrl);
}

function ensureConsentBannerContainer(): void {
  const root = document.getElementById('trustarc-root');
  if (!root) {
    return;
  }

  const existingBanner = root.querySelector('#consent-banner');
  if (existingBanner) {
    return;
  }

  const banner = document.createElement('div');
  banner.id = 'consent-banner';
  root.appendChild(banner);
}

interface TrustArcProviderProps {
  children: ReactNode;
}

export function TrustArcProvider({ children }: TrustArcProviderProps) {
  const [truste, setTruste] = useState<TrustArcInstance | null>(() => getTrustArcInstance());

  const partnerLevel = useSelector(
    (state: any) => state.page?.pageData?.subject?.content?.ga?.dataLayer?.siteType ?? '',
  );
  const id = getTrustArcId(partnerLevel);

  useEffect(() => {
    if (typeof document === 'undefined' || !id || id === 'undefined') {
      return;
    }

    if (!loadedTrustArcId) {
      loadedTrustArcId = id;
    }

    const scriptUrl = createTrustArcScriptUrl(loadedTrustArcId);
    let script = findTrustArcScript(scriptUrl);

    if (!script) {
      script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.dataset.trustarcScript = 'true';
      script.dataset.trustarcId = loadedTrustArcId;
      document.head.appendChild(script);
    }

    const onLoad = () => setTruste(getTrustArcInstance());
    const onError = () => setTruste(null);

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    onLoad();

    return () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
  }, [id]);

  const value = useMemo(
    () => ({
      truste,
      isReady: !!truste,
    }),
    [truste],
  );

  return <TrustArcContext.Provider value={value}>{children}</TrustArcContext.Provider>;
}

export function useTrustArc() {
  return useContext(TrustArcContext);
}

export function CookiePreferenceLink() {
  const { truste, isReady } = useTrustArc();

  useEffect(() => {
    if (!isReady) {
      return;
    }

    truste?.eu?.icon?.initialize?.();
  }, [isReady, truste]);

  return <div id="teconsent" />;
}

export function TrustArcConsentBanner() {
  const { truste, isReady } = useTrustArc();

  useEffect(() => {
    if (typeof document === 'undefined' || !isReady) {
      return;
    }

    ensureConsentBannerContainer();
    if (truste?.bn?.openBanner) {
      truste.bn.openBanner();
      return;
    }

    truste?.bn?.bannerMain?.();
  }, [isReady, truste]);

  return null;
}
